import { useEffect, useRef } from 'react'
import { navigate, roomPath } from './router'
import { collectArtefactAnchors, type ArtefactAnchor, type OpenPart } from './artefact-anchors'

/**
 * Following a link to an anchor (AB#350) — Ctrl/Cmd+click, the AB#327 gesture, in prose or slides.
 *
 * The editor of the open part first tries to reveal the anchor itself (its own `reveal`): found
 * there, it scrolls / goes to the slide and selects it. Otherwise the artefact's anchors are read
 * (lib/artefact-anchors.ts) to find the part holding it; that part is opened (navigate), and the
 * anchor waits here, PENDING, until the editor mounted on that room takes it ({@link usePendingAnchor})
 * and reveals it. An anchor found nowhere is a broken link: the caller says so (a toast), nothing else.
 */

/** An anchor waiting for its part's editor to mount. */
interface Pending {
  room: string
  anchorId: string
  /** When it was set: a pending anchor older than PENDING_TTL_MS is dropped. */
  at: number
}

const PENDING_TTL_MS = 30_000

let pending: Pending | null = null
const listeners = new Set<() => void>()

/** The anchor waiting for `room`'s editor, if any (still fresh). */
export function pendingAnchorFor(room: string): string | null {
  if (!pending || pending.room !== room) return null
  if (Date.now() - pending.at > PENDING_TTL_MS) {
    pending = null
    return null
  }
  return pending.anchorId
}

/** Drop the pending anchor of `room`: its editor revealed it. */
export function clearPendingAnchor(room: string): void {
  if (pending?.room === room) pending = null
}

/** Make `anchorId` wait for `room`'s editor. */
export function setPendingAnchor(room: string, anchorId: string): void {
  pending = { room, anchorId, at: Date.now() }
  for (const l of listeners) l()
}

export type FollowOutcome = 'shown' | 'opened' | 'broken'

/**
 * Follow a link to `anchorId` from the part open here. `revealHere` is the open editor's own
 * reveal (true: found and shown). Else the anchor is looked up in the artefact: in another part,
 * that part is opened and the anchor left pending for its editor ('opened'); in no part, 'broken'.
 * `known`: an anchor list already at hand (the link window's), to spare reading every room again.
 */
export async function followAnchor(
  anchorId: string,
  open: OpenPart,
  wsUrl: string,
  revealHere: (anchorId: string) => boolean,
  known?: readonly ArtefactAnchor[] | null,
): Promise<FollowOutcome> {
  if (revealHere(anchorId)) return 'shown'
  const list = known?.some((a) => a.id === anchorId) ? known : (await collectArtefactAnchors(open, wsUrl)).anchors
  const target = list.find((a) => a.id === anchorId)
  if (!target) return 'broken'
  if (target.room === open.room) return revealHere(anchorId) ? 'shown' : 'broken'
  setPendingAnchor(target.room, anchorId)
  navigate(roomPath(target.room))
  return 'opened'
}

/**
 * For the editor mounted on `room`: reveal the anchor a followed link left pending for it, as soon
 * as `reveal` can find it — on mount, then every `retryMs` while its content syncs, until it is
 * found or stale. `reveal` returns true once shown.
 */
export function usePendingAnchor(room: string, reveal: (anchorId: string) => boolean, ready = true, retryMs = 250): void {
  const revealRef = useRef(reveal)
  revealRef.current = reveal
  useEffect(() => {
    if (!ready) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const attempt = (): void => {
      clearTimeout(timer)
      const id = pendingAnchorFor(room)
      if (!id) return
      if (revealRef.current(id)) clearPendingAnchor(room)
      else timer = setTimeout(attempt, retryMs)
    }
    attempt()
    listeners.add(attempt)
    return () => {
      clearTimeout(timer)
      listeners.delete(attempt)
    }
  }, [room, ready, retryMs])
}
